import { Box, SxProps, Theme, useTheme } from '@mui/material';
import { ComponentType } from 'react';

import { IconProps } from '@/components/icons/ChevronDownIcon'; // Assuming this path is correct or IconProps is defined elsewhere
import { COLORS } from '@/styles/tokens/colors';

interface BoxedIconProps {
  /**
   * The Icon component to render (e.g., UserIcon).
   */
  icon: ComponentType<IconProps>;
  /**
   * Props to pass down to the icon component.
   * The 'color' prop defaults to white but can be overridden.
   * 'size' or other valid IconProps can be passed here.
   */
  iconProps?: Partial<IconProps>;
  /**
   * Optional sx props to customize the wrapping Box element.
   */
  sx?: SxProps<Theme>;
}

/**
 * Renders an icon component within a styled box.
 * The box has a dark navy background and rounded corners.
 * The icon color defaults to white.
 */
export function BoxedIcon({
  icon: IconComponent,
  iconProps,
  sx,
}: BoxedIconProps) {
  const theme = useTheme();

  // Merge default props (white color) with provided iconProps
  // User-provided props will override the default color.
  const mergedIconProps: IconProps = {
    color: theme.palette.common.white, // Default color
    ...iconProps, // Spread user props (including potential color override)
  };

  return (
    <Box
      sx={{
        display: 'inline-flex', // Use inline-flex so the box sizes to the icon + padding
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: COLORS.NAVY[700],
        borderRadius: '6px',
        padding: theme.spacing(0.5),
        ...sx,
      }}
    >
      {/* Render the provided icon component with merged props */}
      <IconComponent {...mergedIconProps} />
    </Box>
  );
}
